import useQuery from "../../../../../../hooks/use-query";
import {Box, Button} from "@mui/material";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import DateUtils from "../../../../../../../core/services/date-utils";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {LayersRounded} from "@mui/icons-material";
import Grid2 from "@mui/material/Unstable_Grid2";
import ProductInformationPopover from "../../../../../../components/app-specific/draggable-popover/product-information-popover";
import {routes} from "../../../../../../../core/models/static/routes/app-routes";
import RearrangeEntriesDialog from "../../../../../../components/dialogs/rearrange-entries";
import Api from "../../../../../../../core/services/api/api";
import LoadingLottieAnimation from "../../../../../../components/app-specific/lottie-animations/loading";
import LostSalesAnalyticsDashboardViewChart from "./chart";
import LostSalesAnalyticsDashboardViewTable from "./table";
import LostSalesAnalyticsDashboardViewSearchSection, {LostSalesAnalyticsDateOptionRanges} from "../../../../../../components/search-sections/lost-sales-analytics/dashboard";
import {QueryBuilderOperatorEnum, QueryBuilderQueryConditionTypeEnum} from "../../../../../../../packages/query-builder";
import useSavedComponentsState from "../../../../../../hooks/use-saved-components-state";

const chartIdentifiers = {
    overview: 'overview',
}


const tableIdentifiers = {
    topReportedProductsByQty: 'top-reported-products-by-qty',
    topReportedProductsByValue: 'top-reported-products',
    topReportedCustomersByQty: 'top-reported-customers',
    topReportedCustomersByValue: 'top-reported-customers-by-value',
    topReportedOperatorsByRequest: 'top-reported-operators',
}

const initialRearrangeEntriesDialogState = {
    open: false,
    close: () => void 0,
    title: undefined,
    entries: [],
}

const LostSalesAnalyticsDashboardView = () => {
    const [query, setQuery] = useQuery();
    const [componentOrder, setComponentOrder] = useState({});
    const [rearrangeEntriesDialogState, setRearrangeEntriesDialogState] = useState(initialRearrangeEntriesDialogState)
    const [loadingComponentOrders, setLoadingComponentOrders] = useState(true);
    const {highestVersion: getCachedComponentOrders, save: saveCachedComponentOrders} = useSavedComponentsState({name: "lost-sales-analytics-dashboard-components-order", version: '1.0.0'});
    const cache = useRef({
        charts: Object.fromEntries(Object.entries(chartIdentifiers).map(([, value]) => [value, {}])),
        tables: Object.fromEntries(Object.entries(tableIdentifiers).map(([, value]) => [value, {}])),
    });
    const isMounted = useIsMounted();

    /**
     * Fetches the chart data based on the current query
     * @type {(function(*, *, *): Promise<*[] | undefined>)}
     */
    const fetchChartData = useCallback(async (identifier, forApi, cancelToken) => {
        /**@type{ApiResponseDs}*/
        let response;

        const filtersIdentifier = JSON.stringify(forApi);
        const cachedEntry = cache.current.charts[identifier][filtersIdentifier];
        if (cachedEntry && cachedEntry.cacheDateTime.diff(DateUtils.localNow(), 'hour') < 1) {
            return cachedEntry.data;
        }

        const defaultPeriod = LostSalesAnalyticsDateOptionRanges.last2Weeks();
        defaultPeriod.startDate = DateUtils.UTCToServer(defaultPeriod.startDate).format(DateUtils.withoutOffsetFormat);
        defaultPeriod.endDate = DateUtils.UTCToServer(defaultPeriod.endDate).format(DateUtils.withoutOffsetFormat);

        forApi.period = {
            ...forApi.period,
            startDateTime: forApi.period?.startDateTime ?? defaultPeriod.startDate,
            endDateTime: forApi.period?.endDateTime ?? defaultPeriod.endDate,
        }

        switch (identifier) {
            case chartIdentifiers.overview:
                response = await Api.getLostSalesChart(forApi, cancelToken);
                break;
            default:
                return;
        }
        if (!isMounted())
            return;

        let data = response?.data ?? [];
        if (!Array.isArray(data)) {
            data = data?.items ?? [];
        }
        if (response?.resultFlag) {
            cache.current.charts[identifier][filtersIdentifier] = {
                data: data,
                cacheDateTime: DateUtils.localNow(),
            }
        }
        return data;
    }, [isMounted])

    /**
     * Fetches the table data based on the current query
     * @type {(function(*, *, *): Promise<*[] | undefined>)}
     */
    const fetchTableData = useCallback(async (identifier, forApi, cancelToken) => {
        /**@type{ApiResponseDs}*/
        let response;

        const filtersIdentifier = JSON.stringify(forApi);
        const cachedEntry = cache.current.tables[identifier][filtersIdentifier];
        if (cachedEntry && cachedEntry.cacheDateTime.diff(DateUtils.localNow(), 'hour') < 1) {
            return cachedEntry.data;
        }

        const defaultPeriod = LostSalesAnalyticsDateOptionRanges.last2Weeks();
        defaultPeriod.startDate = DateUtils.UTCToServer(defaultPeriod.startDate).format(DateUtils.withoutOffsetFormat);
        defaultPeriod.endDate = DateUtils.UTCToServer(defaultPeriod.endDate).format(DateUtils.withoutOffsetFormat);

        forApi.period = {
            ...forApi.period,
            startDateTime: forApi.period?.startDateTime ?? defaultPeriod.startDate,
            endDateTime: forApi.period?.endDateTime ?? defaultPeriod.endDate,
        }

        switch (identifier) {
            case tableIdentifiers.topReportedProductsByQty:
                response = await Api.getLostSalesTopReportedProductsByQty(forApi, cancelToken);
                break;
            case tableIdentifiers.topReportedProductsByValue:
                response = await Api.getLostSalesTopReportedProductsByValue(forApi, cancelToken);
                break;
            case tableIdentifiers.topReportedCustomersByQty:
                response = await Api.getLostSalesTopReportedCustomersByQty(forApi, cancelToken);
                break;
            case tableIdentifiers.topReportedCustomersByValue:
                response = await Api.getLostSalesTopReportedCustomersByValue(forApi, cancelToken);
                break;
            case tableIdentifiers.topReportedOperatorsByRequest:
                response = await Api.getLostSalesTopReportedOperatorsByRequest(forApi, cancelToken);
                break;
            default:
                return;
        }
        if (!isMounted())
            return;
        let data = response?.data ?? [];
        if (!Array.isArray(data)) {
            data = data?.items ?? [];
        }
        if (response?.resultFlag) {
            cache.current.tables[identifier][filtersIdentifier] = {
                data: data,
                cacheDateTime: DateUtils.localNow(),
            }
        }
        return data;
    }, [isMounted])

    /**
     * Navigates to the appropriate page based on the identifier
     * @type {(function(*, *): void)}
     */
    const navigateOnClick = useCallback((identifier, entry) => {
        const defaultPeriod = LostSalesAnalyticsDateOptionRanges.last2Weeks();
        defaultPeriod.startDate = DateUtils.UTCToServer(defaultPeriod.startDate).format(DateUtils.withoutOffsetFormat);
        defaultPeriod.endDate = DateUtils.UTCToServer(defaultPeriod.endDate).format(DateUtils.withoutOffsetFormat);
        const startDateTime = query.startDate ?? defaultPeriod.startDate;
        const endDateTime = query.endDate ?? defaultPeriod.endDate;

        const newQuery = {
            keywords: "",
            filters: [
                {
                    condition: QueryBuilderQueryConditionTypeEnum.AND,
                    rules: [
                        {
                            "column": "Submit Date Time",
                            "field": "submitDateTime",
                            "operator": QueryBuilderOperatorEnum.greaterThanOrEqual,
                            "value": startDateTime,
                            "multiSelect": false,
                            "isNullable": false,
                            "type": "DateTime",
                            "displayEmptyValuesOnly": false
                        },
                        {
                            "column": "Submit Date Time",
                            "field": "submitDateTime",
                            "operator": QueryBuilderOperatorEnum.lessThanOrEqual,
                            "value": endDateTime,
                            "multiSelect": false,
                            "isNullable": false,
                            "type": "DateTime",
                            "displayEmptyValuesOnly": false
                        }
                    ]
                }
            ],
        };
        switch (identifier) {
            case tableIdentifiers.topReportedProductsByQty:
            case tableIdentifiers.topReportedProductsByValue:
                newQuery.filters[0].rules.push({
                    "column": "Part Number",
                    "field": "partId",
                    "operator": QueryBuilderOperatorEnum.in,
                    "value": [{"key": entry.partId, "value": entry.partNumber}],
                    "multiSelect": true,
                    "isNullable": false,
                    "type": "List",
                    "displayEmptyValuesOnly": false
                })
                break;
            case tableIdentifiers.topReportedCustomersByQty:
            case tableIdentifiers.topReportedCustomersByValue:
                newQuery.filters[0].rules.push({
                    "column": "Customer",
                    "field": "customerName",
                    "operator": QueryBuilderOperatorEnum.equal,
                    "value": entry.customerName,
                    "multiSelect": false,
                    "isNullable": false,
                    "type": "String",
                    "displayEmptyValuesOnly": false
                })
                break;
            case tableIdentifiers.topReportedOperatorsByRequest:
                newQuery.filters[0].rules.push({
                    "column": "Operator Firstname",
                    "field": "operatorId",
                    "operator": QueryBuilderOperatorEnum.in,
                    "value": [{"key": entry.operatorId, "value": entry.operatorName,}],
                    "multiSelect": true,
                    "isNullable": true,
                    "type": "List",
                    "displayEmptyValuesOnly": false
                })
                break;
            default:
                break;
        }
        setQuery(prevState => ({
            startDate: prevState.startDate,
            endDate: prevState.endDate,
            filters: JSON.stringify(newQuery),
        }), {location: {pathname: routes.analytics.lostSales.logs}});
    }, [query.endDate, query.startDate, setQuery])

    const chartDefinitions = useMemo(() => ({
        [chartIdentifiers.overview]: {
            identifier: chartIdentifiers.overview,
            title: "Overview",
            datasetEntries: {
                totalRequests: {title: 'Total Requests',},
            },
        }
    }), [])

    const tableDefinitions = useMemo(() => ({
        [tableIdentifiers.topReportedProductsByQty]: {
            identifier: tableIdentifiers.topReportedProductsByQty,
            title: 'Top Reported Products by Quantity',
            columnDefinitions: {
                partNumber: {title: 'Part No', orderIndex: 1, render: (e) => <ProductInformationPopover partNo={e.partNumber}>{e.partNumber ?? ""}</ProductInformationPopover>},
                totalQuantity: {title: 'Total Qty', orderIndex: 2},
            },
        },
        [tableIdentifiers.topReportedProductsByValue]: {
            identifier: tableIdentifiers.topReportedProductsByValue,
            title: 'Top Reported Products by Value',
            columnDefinitions: {
                partNumber: {title: 'Part No', orderIndex: 1, render: (e) => <ProductInformationPopover partNo={e.partNumber}>{e.partNumber ?? ""}</ProductInformationPopover>},
                totalValue: {title: 'Total Value', orderIndex: 2},
            },
        },
        [tableIdentifiers.topReportedCustomersByQty]: {
            identifier: tableIdentifiers.topReportedCustomersByQty,
            title: 'Top Reported Customers by Quantity',
            columnDefinitions: {
                customerName: {title: 'Customer', orderIndex: 1},
                totalQuantity: {title: 'Total Qty', orderIndex: 2},
            },
        },
        [tableIdentifiers.topReportedCustomersByValue]: {
            identifier: tableIdentifiers.topReportedCustomersByValue,
            title: 'Top Reported Customers by Value',
            columnDefinitions: {
                customerName: {title: 'Customer', orderIndex: 1},
                totalValue: {title: 'Total Value', orderIndex: 2},
            },
        },
        [tableIdentifiers.topReportedOperatorsByRequest]: {
            identifier: tableIdentifiers.topReportedOperatorsByRequest,
            title: 'Top Reported Operators by Request',
            columnDefinitions: {
                operatorName: {title: 'Operator', orderIndex: 1},
                totalRequests: {title: 'Total Requests', orderIndex: 2},
            },
        },
    }), [])

    const dashboardComponents = useMemo(() => {
        const components = [
            {
                identifier: chartIdentifiers.overview,
                definitions: chartDefinitions[chartIdentifiers.overview],
                type: 'chart',
                size: {xs: 12, md: 12, xl: 12},
            },
            ...Object.values(tableIdentifiers).map((e) => ({
                identifier: e,
                type: 'table',
                definitions: tableDefinitions[e],
                size: {xs: 12, md: 6, xl: 4},
            })),
        ];
        components.sort((a, b) => componentOrder[a.identifier] - componentOrder[b.identifier]);
        return components;
    }, [chartDefinitions, componentOrder, tableDefinitions])


    /**
     * Fetches the component orders from the server
     * @type {(function(): Promise<void>)}
     */
    const getComponentOrders = useCallback(async () => {
        setLoadingComponentOrders(true);
        const cachedComponentOrders = await getCachedComponentOrders();
        if (!isMounted())
            return;
        if (cachedComponentOrders?.data) {
            setComponentOrder(cachedComponentOrders?.data ?? {})
        }
        setLoadingComponentOrders(false);
    }, [getCachedComponentOrders, isMounted])

    /**
     * As soon as the component is mounted, fetch the component orders
     */
    useEffect(() => {
        void getComponentOrders();
    }, [getComponentOrders])

    /**
     * Saves the component orders in the server
     * @type {(function(Record<string, int>): Promise<void>)}
     */
    const saveComponentsOrder = useCallback(async (componentsInOrder) => {
        const componentsOrder = Object.fromEntries(componentsInOrder.map((e, index) => ([e.key, index + 1])))
        setComponentOrder(componentsOrder ?? {})
        void saveCachedComponentOrders(componentsOrder);
    }, [saveCachedComponentOrders])

    /**
     * Opens the rearrange entries dialog
     * @type {(function(): void)}
     */
    const openRearrangeEntriesDialog = useCallback(() => {
        setRearrangeEntriesDialogState({
            open: true,
            close: () => setRearrangeEntriesDialogState(initialRearrangeEntriesDialogState),
            title: "Rearrange Dashboard Entries",
            entries: dashboardComponents.map(e => ({
                key: e.identifier,
                title: e.definitions?.title ?? "",
                size: e.size.xl,
            })),
            onRearrange: saveComponentsOrder,
        });
    }, [dashboardComponents, saveComponentsOrder])

    return (
        <>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                    <Box/>
                    <Button
                        size={'small'}
                        variant={'outlined'}
                        color={'primary'}
                        sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5}}
                        onClick={() => openRearrangeEntriesDialog()}
                    >
                        <LayersRounded/>
                        Rearrange components
                    </Button>
                </Box>
                <LostSalesAnalyticsDashboardViewSearchSection filters={query} setFilters={setQuery}/>
                {
                    loadingComponentOrders &&
                    <LoadingLottieAnimation width={'min(90%, 500px)'}/>
                }
                {
                    !loadingComponentOrders &&
                    <Grid2 container spacing={3}>
                        {
                            dashboardComponents.map(e => (
                                <Grid2 key={`${e.type}-${e.identifier}`} {...e.size}>
                                    {
                                        e.type === 'chart' &&
                                        <LostSalesAnalyticsDashboardViewChart
                                            filters={query}
                                            fetchData={fetchChartData}
                                            {...e.definitions}
                                        />
                                    }
                                    {
                                        e.type === 'table' &&
                                        <LostSalesAnalyticsDashboardViewTable
                                            filters={query}
                                            navigateOnClick={navigateOnClick}
                                            fetchData={fetchTableData}
                                            {...e.definitions}
                                        />
                                    }
                                </Grid2>
                            ))
                        }
                    </Grid2>
                }
            </Box>
            <RearrangeEntriesDialog
                open={rearrangeEntriesDialogState.open}
                close={rearrangeEntriesDialogState.close}
                title={rearrangeEntriesDialogState.title}
                entries={rearrangeEntriesDialogState.entries}
                onRearrange={rearrangeEntriesDialogState.onRearrange}
            />
        </>
    );
}

export default LostSalesAnalyticsDashboardView;